/**
 * VehicleModalSidebar - Sidebar contextual do modal premium de Veículo
 *
 * Posicionada à DIREITA, padronizada 100% com o CustomerModalSidebar v2.0.0
 * (cards flutuantes `SectionCard`, linhas `QuickInfoRow`, largura 360px).
 *
 * @version 2.1.0
 * RELEASE: 2026-09-29 v2.1.0 - Foto de perfil do veículo movida do header para a sidebar:
 *          nova linha "Foto do veículo" como PRIMEIRA opção da seção Identificação
 *          (VehiclePhotoField layout="row"). Edição condicionada à permissão canEditPhoto.
 * RELEASE: 2026-08-08 v2.0.0 - Padronização visual com CustomerModalSidebar
 *   - Sidebar movida para a DIREITA (`border-l`, `bg-muted/30`, 360px)
 *   - Seções agrupadas em cards flutuantes (sem `<Separator />`)
 *   - Linhas de informação no formato rótulo/valor compacto
 *   - Novo card "Ações Rápidas" com botão tracejado "Novo Negócio"
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (sidebar à esquerda)
 */

import React, { useState } from 'react';
import {
  Car,
  Hash,
  Palette,
  Fuel,
  User,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  FileText,
  Sparkles,
  Plus,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { SocialNetworkBadge } from '@/components/ui/social-network-badge';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
// v2.1.0: foto de perfil do veículo (primeira opção da seção Identificação)
import { VehiclePhotoField } from '@/components/vehicles/VehiclePhotoField';
import type { VehicleDetailsVehicle } from '@/hooks/useVehicleDetails';

interface VehicleModalSidebarProps {
  vehicle: VehicleDetailsVehicle;
  /** Permite exibir o botão de criação de negócio (permissão do usuário) */
  canCreateDeal?: boolean;
  /** Disparado após criar um negócio para revalidar contadores */
  onDealCreated?: () => void;
  /**
   * v2.1.0: permite adicionar/trocar/remover a foto de perfil do veículo
   * (mesma permissão usada pelo header — canTransferVehicle)
   */
  canEditPhoto?: boolean;
}

/** Formata valores monetários em BRL */
const formatCurrency = (value: number | null) =>
  value == null
    ? null
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/**
 * SectionCard — wrapper visual reutilizado em todas as seções
 * (mesmo padrão do CustomerModalSidebar v2.0.0 / DealModalSidebar v3.0.0)
 */
function SectionCard({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {action ?? (Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />)}
      </div>
      {children}
    </div>
  );
}

/** Linha de informação rápida (label à esquerda + valor à direita) */
function QuickInfoRow({
  icon: Icon,
  label,
  value,
  className,
}: {
  icon: typeof Phone;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  if (!value) return null;

  return (
    <div className={cn('flex items-center gap-2 py-1.5 w-full', className)}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs font-medium ml-auto truncate max-w-[180px]">{value}</span>
    </div>
  );
}

/**
 * Sidebar contextual (360px fixa à DIREITA) — padrão visual unificado
 */
export function VehicleModalSidebar({
  vehicle,
  canCreateDeal = false,
  onDealCreated,
  canEditPhoto = false,
}: VehicleModalSidebarProps) {
  const [newDealModalOpen, setNewDealModalOpen] = useState(false);
  // Modal de detalhes do cliente/contato aberto pelo card de identidade
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);

  const fipeValue = formatCurrency(vehicle.fipe_value);
  // Dados do proprietário exibidos no card de identidade
  const ownerName = vehicle.customer_name || vehicle.contact_name || null;
  const ownerPhone =
    formatPhoneForDisplay(vehicle.contact_phone) || vehicle.contact_phone || null;

  return (
    <>
      {/* Mobile: largura total e sem borda lateral (é injetada dentro da aba Resumo) */}
      <ScrollArea className="w-full md:w-[360px] h-auto md:h-full shrink-0 bg-muted/30 rounded-lg md:rounded-none border md:border-l md:border-t-0 md:border-r-0 md:border-b-0 border-border/40 relative z-10">
        <div className="p-3 space-y-3">
          {/* ═══════════════════════════════════════════ */}
          {/* IDENTIDADE — Dados completos do cliente/    */}
          {/* contato (mesmo formato do modal de cliente) */}
          {/* Clicável: abre o modal de cliente/contato   */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title={ownerName ? (vehicle.customer_name ? 'Cliente' : 'Contato') : 'Proprietário'} icon={User}>
            {ownerName ? (
              <div
                role="button"
                tabIndex={0}
                title="Abrir detalhes do cliente/contato"
                className="rounded-lg -m-1 p-1 cursor-pointer transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setOwnerModalOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOwnerModalOpen(true);
                  }
                }}
              >
                <div className="flex flex-col items-center text-center pt-1">
                  <SmartAvatar
                    src={vehicle.contact_avatar_url || undefined}
                    name={ownerName}
                    size="lg"
                    className="h-16 w-16 mb-2"
                  />
                  <h3 className="text-sm font-semibold">{ownerName}</h3>
                  {/* Tipo de pessoa + documento (somente quando há cliente) */}
                  {vehicle.customer_id && vehicle.customer_type && (
                    <span className="text-xs text-muted-foreground">
                      {vehicle.customer_type === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                    </span>
                  )}
                  {vehicle.customer_document && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {vehicle.customer_type === 'pj' ? 'CNPJ' : 'CPF'}:{' '}
                      {formatDocumentForDisplay(vehicle.customer_document)}
                    </span>
                  )}
                  {vehicle.customer_name && vehicle.contact_name && (
                    <span className="text-xs text-muted-foreground mt-0.5">
                      Contato: {vehicle.contact_name}
                    </span>
                  )}
                </div>

                {/* Telefone, e-mail e redes sociais (Instagram etc.) */}
                <div className="mt-2 space-y-0.5">
                  <QuickInfoRow icon={Phone} label="Telefone" value={ownerPhone} />
                  <QuickInfoRow icon={Mail} label="E-mail" value={vehicle.contact_email} />
                  <SocialNetworkBadge
                    channelIds={vehicle.contact_channel_ids as any}
                    metadata={vehicle.contact_metadata as any}
                    variant="default"
                    showLabel
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum proprietário vinculado</p>
            )}
          </SectionCard>


          {/* ═══════════════════════════════════════════ */}
          {/* IDENTIFICAÇÃO                              */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Identificação" icon={Hash}>
            <div className="space-y-0.5">
              {/* v2.1.0: foto de perfil do veículo — primeira opção da seção (movida do header) */}
              <VehiclePhotoField
                vehicleId={vehicle.id}
                tenantId={vehicle.tenant_id}
                canEdit={canEditPhoto}
                layout="row"
              />
              <QuickInfoRow icon={Hash} label="Placa" value={vehicle.plate} />
              {/* Marca e Modelo exibidos em campos separados */}
              <QuickInfoRow icon={Car} label="Marca" value={vehicle.brand_name} />
              <QuickInfoRow icon={Car} label="Modelo" value={vehicle.model_name} />
              <QuickInfoRow icon={Calendar} label="Ano" value={vehicle.year} />
              <QuickInfoRow icon={Palette} label="Cor" value={vehicle.color} />
              <QuickInfoRow icon={Fuel} label="Combustível" value={vehicle.fuel} />
            </div>
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* DOCUMENTAÇÃO E FIPE                        */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Documentação e FIPE" icon={FileText}>
            <div className="space-y-0.5">
              <QuickInfoRow icon={Hash} label="Renavam" value={vehicle.renavam} />
              <QuickInfoRow icon={Hash} label="Chassi" value={vehicle.chassis} />
              <QuickInfoRow icon={Hash} label="Cód. FIPE" value={vehicle.fipe_code} />
              <QuickInfoRow icon={DollarSign} label="Valor FIPE" value={fipeValue} />
            </div>
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* AÇÕES RÁPIDAS — Novo Negócio               */}
          {/* ═══════════════════════════════════════════ */}
          {canCreateDeal && (
            <SectionCard title="Ações Rápidas" icon={Sparkles}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-10 text-xs text-muted-foreground border-2 border-dashed border-muted-foreground/30 rounded-lg hover:border-muted-foreground/50 hover:bg-muted/30 transition-colors"
                onClick={() => setNewDealModalOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Novo Negócio
              </Button>
            </SectionCard>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* OBSERVAÇÕES                                */}
          {/* ═══════════════════════════════════════════ */}
          {vehicle.notes && (
            <SectionCard title="Observações" icon={FileText}>
              <p className="whitespace-pre-wrap text-xs text-muted-foreground">{vehicle.notes}</p>
            </SectionCard>
          )}
        </div>
      </ScrollArea>

      {/* Criação de negócio já vinculada ao contato do veículo */}
      {newDealModalOpen && (
        <DealFormDialog
          open={newDealModalOpen}
          onOpenChange={setNewDealModalOpen}
          defaultContactId={vehicle.contact_id ?? undefined}
          onSuccess={() => {
            // v3.5.0: fechamento controlado pelo formulário (modal de sucesso)
            onDealCreated?.();
          }}
        />
      )}

      {/*
        Detalhes do cliente/contato — abre por cima do modal do veículo.
        Com cliente vinculado usa o modo cliente; sem cliente, o modo contato.
      */}
      {ownerModalOpen && (
        <CustomerDetailsModalPremium
          open={ownerModalOpen}
          onOpenChange={setOwnerModalOpen}
          customerId={vehicle.customer_id ?? null}
          contactId={vehicle.customer_id ? null : vehicle.contact_id ?? null}
          contactPreview={
            !vehicle.customer_id && vehicle.contact_id
              ? {
                  id: vehicle.contact_id,
                  name: vehicle.contact_name,
                  phone: vehicle.contact_phone,
                  email: vehicle.contact_email ?? null,
                  avatar_url: vehicle.contact_avatar_url ?? null,
                }
              : null
          }
        />
      )}
    </>

  );
}
